import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import TablePagination from '../components/TablePagination';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiDollarSign } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PAGE_SIZE = 15;

const DepositsHistory = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const totalPages = Math.max(1, Math.ceil(transactions.length / PAGE_SIZE));
  useEffect(() => {
    setPage((p) => (p > totalPages ? 1 : p));
  }, [transactions.length, totalPages]);

  const pagedTransactions = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return transactions.slice(start, start + PAGE_SIZE);
  }, [transactions, page]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/payments/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      setTransactions([]);
      toast.error(error.response?.data?.message || 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (transaction) => {
    const serverId = typeof transaction.serverId === 'object' && transaction.serverId !== null
      ? transaction.serverId._id
      : transaction.serverId;

    if ((transaction.type === 'server_purchase' || transaction.type === 'server_renewal') && serverId) {
      navigate(`/server/${serverId}`);
    } else if (transaction.type === 'deposit') {
      const paymentUrl = transaction.paymentUrl;
      const trackId = transaction.oxapayTrackId || transaction.hoodpayTransactionId;
      if (paymentUrl) {
        window.open(paymentUrl, '_blank');
      } else if (trackId) {
        window.open(paymentUrl || '#', '_blank');
      } else {
        toast.error('Payment URL not available for this transaction');
      }
    } else {
      toast.success(`Transaction Details: ${transaction.description || 'N/A'}`, { duration: 5000 });
    }
  };

  const getStatusStyle = (status) => {
    const st = String(status || '').toLowerCase();
    switch (st) {
      case 'completed': return 'border-[#009439] text-[#009439] bg-[#00943942]';
      case 'failed':
      case 'expired':
      case 'cancelled':
        return 'border-[#940000] text-[#DF0A0A] bg-[#94000042]';
      case 'pending':   return 'border-[#946800] text-[#DFA50A] bg-[#94680042]';
      default:          return 'border-blue-400/35 text-blue-200 bg-blue-500/15';
    }
  };

  const getStatusLabel = (status) => {
    const st = String(status || '').toLowerCase();
    switch (st) {
      case 'completed': return 'Paid';
      case 'failed':    return 'Failed';
      case 'expired':   return 'Expired';
      case 'cancelled': return 'Cancelled';
      case 'pending':   return 'Pending';
      default:          return status || '—';
    }
  };

  // Payment method icon
  const PaymentIcon = ({ method }) => {
    if (method === 'Crypto') {
      return (
        <div className="w-[29px] h-[29px] rounded-full bg-gradient-to-t from-[#3C3CFFC2_10%] to-[#3C3CFF00_50%] border border-[#FFFFFF2B] flex items-center justify-center shrink-0">
          <svg width="12" height="16" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11.9686 6.4323C12.2183 4.75108 10.9475 3.8473 9.2099 3.24438L9.77356 0.966957L8.39733 0.621474L7.84856 2.83888C7.48677 2.74807 7.11517 2.66239 6.74593 2.5775L7.29862 0.345483L5.92317 0L5.35911 2.27663C5.05964 2.20793 4.76566 2.14002 4.4803 2.06855L4.48187 2.06145L2.58391 1.58409L2.21781 3.06473C2.21781 3.06473 3.23891 3.30045 3.21735 3.31506C3.77474 3.45522 3.87548 3.82676 3.85863 4.12131L3.21657 6.71579C3.25498 6.72566 3.30476 6.73988 3.35964 6.76199C3.31378 6.75054 3.26478 6.7379 3.21421 6.72566L2.31423 10.3601C2.24603 10.5307 2.07317 10.7866 1.68354 10.6894C1.69726 10.7096 0.683217 10.4379 0.683217 10.4379L0 12.0248L1.79094 12.4745C2.12413 12.5586 2.45064 12.6466 2.77206 12.7296L2.20252 15.033L3.57719 15.3785L4.14124 13.0995C4.51676 13.2022 4.88129 13.2969 5.23799 13.3862L4.6759 15.6545L6.05213 16L6.62168 13.7009C8.96844 14.1482 10.7331 13.9678 11.4759 11.8297C12.0745 10.1082 11.4461 9.11522 10.2114 8.46768C11.1106 8.25882 11.7879 7.66301 11.9686 6.4323ZM8.8242 10.8738C8.3989 12.5953 5.52139 11.6647 4.58849 11.4313L5.34422 8.37964C6.27713 8.61417 9.2687 9.0785 8.8242 10.8738ZM9.24988 6.40742C8.86182 7.97335 6.46684 7.17775 5.68994 6.9827L6.37512 4.21489C7.15202 4.40994 9.65401 4.77398 9.24988 6.40742Z" fill="white"/>
          </svg>

        </div>
      );
    }
    return (
      <div className="w-[29px] h-[29px] rounded-full bg-gradient-to-t from-[#3C3CFFC2_10%] to-[#3C3CFF00_50%] border border-[#FFFFFF2B] flex items-center justify-center shrink-0">
        <svg width="9" height="15" viewBox="0 0 9 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.04034 13.3889H3.27865C1.47437 13.3889 0.011055 11.8712 0.011055 10.0006C0.011055 9.54991 0.385031 9.17579 0.836243 9.17579C1.28731 9.17579 1.6613 9.54991 1.6613 10.0006C1.6613 10.9571 2.38757 11.7393 3.27865 11.7393H6.04034C6.75542 11.7393 7.34962 11.1002 7.34962 10.3307C7.34962 9.37277 7.00852 9.18573 6.44734 8.98875L2.01345 7.42562C1.15524 7.12944 0 6.49035 0 4.52196C0 2.82709 1.33125 1.4624 2.95966 1.4624H5.72121C7.52563 1.4624 8.98895 2.98154 8.98895 4.85073C8.98895 5.30279 8.61483 5.67692 8.16376 5.67692C7.71269 5.67692 7.33857 5.30279 7.33857 4.85073C7.33857 3.89417 6.61244 3.11332 5.72121 3.11332H2.95966C2.24444 3.11332 1.65038 3.75105 1.65038 4.52196C1.65038 5.47852 1.99148 5.66557 2.55252 5.86397L6.98655 7.42562C7.84462 7.72322 9 8.36095 9 10.3307C8.98895 12.0143 7.66862 13.3889 6.04034 13.3889Z" fill="white"/>
        <path d="M4.49938 14.8529C4.04831 14.8529 3.67419 14.4788 3.67419 14.0267V0.824766C3.67419 0.372704 4.04831 0 4.49938 0C4.95045 0 5.32457 0.372704 5.32457 0.824766V14.0267C5.32457 14.4788 4.95045 14.8529 4.49938 14.8529Z" fill="white"/>
        </svg>

      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <div className="flex shrink-0 items-center justify-between animate-enterUp">
          <h1 className="text-xl md:text-4xl font-light text-white">Transactions</h1>
          <button
            type="button"
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('myrdp:open-deposit'));
              }
            }}
            className="rounded-lg md:w-[165px] flex items-center justify-center px-4 py-2 text-sm text-blue-100 
            bg-[rgba(255,255,255,0.06)]
            border border-blue-300/40
            shadow-[inset_0_1px_1px_rgba(255,255,255,0.23),0_2px_4px_rgba(0,0,0,0.25),inset_0_-16px_28px_rgba(255,255,255,0.15)]
            hover:bg-[rgba(255,255,255,0.10)]
            transition-all duration-200"
          >
            Deposit
          </button>          
        </div>

        {transactions.length === 0 ? (
          <div className="rdp-card flex min-h-0 flex-1 flex-col items-center justify-center p-12 text-center animate-enterZoom" style={{ animationDelay: '90ms' }}>
            <FiDollarSign className="mx-auto text-blue-300/60 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-white mb-2">No transactions yet</h3>
            <p className="text-blue-100/70">Your transaction history will appear here</p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[#B8B8B847] bg-[#212A5533] animate-enterUp" style={{ animationDelay: '120ms' }}>
            <div className="min-h-0 flex-1 overflow-x-auto overflow-y-auto p-4 font-geist">
              <div className="inline-block min-w-full px-2 sm:px-4">
                <table className="min-w-full border-separate border-spacing-y-3">
                  <thead
                    className="text-[#BABABA] sticky top-0 z-10 backdrop-blur-md"
                    style={{ background: 'rgba(33,42,85,0.45)' }}
                  >
                    <tr>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-medium text-xs sm:text-sm">No</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-medium text-xs sm:text-sm">Amount</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-medium text-xs sm:text-sm">Type</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-medium text-xs sm:text-sm">Payment Method</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-medium text-xs sm:text-sm">Status</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-medium text-xs sm:text-sm">Created at</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-medium text-xs sm:text-sm">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedTransactions.map((transaction, idx) => (
                      <tr key={transaction._id}>
                        {/* No */}
                        <td className="px-2 sm:px-4 py-2 sm:py-2 bg-[#FFFFFF14] border-y border-[#EBEBEB1C] rounded-l-2xl text-white text-xs sm:text-sm">
                          {String((page - 1) * PAGE_SIZE + idx + 1).padStart(2, '0')}
                        </td>

                        {/* Amount — pill */}
                        <td className="px-2 sm:px-4 py-2 sm:py-3 bg-[#FFFFFF14] border-y border-[#EBEBEB1C]">
                          {(() => {
                            const st = String(transaction.status || '').toLowerCase();
                            const isPending = st === 'pending';
                            const isFailedLike = st === 'failed' || st === 'expired' || st === 'cancelled';
                            const useClockPill = isPending || isFailedLike;
                            const amt = typeof transaction.amount === 'number' ? transaction.amount : 0;
                            const amountStr = `€${Math.abs(amt).toFixed(2)}`;

                            const pillClass = isPending
                              ? 'border-[#946800] text-[#DFA50A] bg-[#94680042]'
                              : isFailedLike
                                ? 'border-[#940000] text-[#DF0A0A] bg-[#94000042]'
                                : (amt > 0
                                  ? 'border-[#009439] text-[#009439] bg-[#00943942]'
                                  : 'border-[#940000] text-[#DF0A0A] bg-[#94000042]');

                            return (
                              <span className={` inline-block rounded-full px-3 py-1 text-xs sm:text-sm border ${pillClass}`}>
                                {useClockPill ? (
                                  <span className="inline-flex items-center gap-1">
                                    {st === 'expired' ? (
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path
                                          d="M18 6L6 18M6 6l12 12"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    ) : (
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path
                                          d="M12 8v5l3 2"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M21 12a9 9 0 1 1-18 0a9 9 0 0 1 18 0Z"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                        />
                                      </svg>
                                    )}
                                    {amountStr}
                                  </span>
                                ) : (
                                  <span>
                                    {amt > 0 ? '+' : '-'}{amountStr}
                                  </span>
                                )}
                              </span>
                            );
                          })()}
                        </td>

                        {/* Type */}
                        <td className="px-2 sm:px-4 py-2 sm:py- bg-[#FFFFFF14] border-y border-[#EBEBEB1C] text-white text-xs sm:text-sm capitalize">
                          {transaction.type.replace(/_/g, ' ')}
                        </td>

                        {/* Payment Method — icon + label */}
                        <td className="px-2 sm:px-4 py-2 sm:py- bg-[#FFFFFF14] border-y border-[#EBEBEB1C]">
                          {(() => {
                            const raw = transaction.paymentMethod;
                            const lower = String(raw || '').toLowerCase();
                            const label = !raw ? 'Balance' : (lower === 'oxapay' || lower === 'hoodpay' ? 'Cryptocurrency' : raw);
                            const iconMethod = (lower === 'oxapay' || lower === 'hoodpay') ? 'Crypto' : raw;
                            return (
                          <div className="flex items-center gap-2 text-white text-xs sm:text-sm">
                            <PaymentIcon method={iconMethod} />
                            <span>{label}</span>
                          </div>
                            );
                          })()}
                        </td>

                        {/* Status */}
                        <td className="px-2 sm:px-4 py-2 sm:py- bg-[#FFFFFF14] border-y border-[#EBEBEB1C]">
                          <div className="flex items-center justify-center">
                            <span className={`rounded-full border px-3 sm:px-5 py-1 text-xs sm:text-sm ${getStatusStyle(transaction.status)}`}>
                              {getStatusLabel(transaction.status)}
                            </span>
                          </div>
                        </td>

                        {/* Created At */}
                        <td className="px-2 sm:px-4 py-2 sm:py- bg-[#FFFFFF14] border-y border-[#EBEBEB1C] text-white text-xs sm:text-sm whitespace-nowrap">
                          {new Date(transaction.createdAt).toLocaleDateString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric'
                          })}
                        </td>

                        {/* Details — circle i button */}
                        <td className="px-2 sm:px-4 py-2 sm:py- bg-[#FFFFFF14] border-y border-[#EBEBEB1C] rounded-r-xl">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => handleViewDetails(transaction)}
                              className="flex items-center justify-center hover:opacity-70 transition-opacity"
                            >
                              <svg width="25" height="27" viewBox="0 0 25 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M12.5 0C5.6 0 0 5.83172 0 13.0172C0 20.2028 5.6 26.0345 12.5 26.0345C19.4 26.0345 25 20.2028 25 13.0172C25 5.83172 19.4 0 12.5 0ZM13.7504 19.5258H11.2504V11.7154H13.7504V19.5258ZM11.2504 9.11207H13.7504V6.50862H11.2504V9.11207Z" fill="#A4A4A4"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <TablePagination
              page={page}
              pageSize={PAGE_SIZE}
              total={transactions.length}
              onPageChange={setPage}
              className="shrink-0 rounded-b-2xl bg-[#0d1229]/35"
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DepositsHistory;